/* eslint-disable react/jsx-fragments */

/* eslint-disable no-shadow */
// prettier-ignore
import FullCalendar from '@fullcalendar/react';

import styled from '@emotion/styled';
import plLocale from '@fullcalendar/core/locales/pl';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import timeGridPlugin from '@fullcalendar/timegrid';
import PropTypes from 'prop-types';
import React from 'react';

import 'react-big-calendar/lib/css/react-big-calendar.css';

import { Card, Container, Flex, Spinner, ThemeProvider } from 'theme-ui';

import {
  CalendarLegendModal,
  CalendarTooltip,
  DimLoadingIndicator,
  Layout,
  SEO,
  VisitPreviewWebCalendar,
} from '../../../components';
import { STRINGS as COMMON_STRINGS, STRINGS } from '../../../constants';
import theme from '../../../theme';
import styles from './VisitsCalendarScreen.style';

export const StyleWrapper = styled.div`
  // calendar
  .fc-day-today,
  .fc-col-header-cell,
  .fc-timegrid-axis,
  .fc-list-day-cushion {
    background: ${({ theme }) => theme.colors.content_bg} !important;
    font-weight: 400 !important;
  }
  .fc-timegrid-slot,
  .fc-daygrid-day,
  .fc-day {
    border: ${({ theme }) => theme.colors.calendar_border} 0.25px solid !important;
  }
  // toolbar
  .fc div > h2 {
    font-weight: 300;
  }
  .fc-button {
    font-weight: 300;
    background: ${({ theme }) => theme.colors.calendar_button};
    color: ${({ theme }) => theme.colors.calendar_text};
  }
  .fc-button:focus {
    box-shadow: 0 0 0 0 transparent !important;
  }
  .fc-button-active {
    font-weight: 400;
    background: ${({ theme }) => theme.colors.calendar_button_active} !important;
    color: ${({ theme }) => theme.colors.calendar_text} !important;
  }
  .fc-today-button {
    font-weight: 400;
    background: ${({ theme }) => theme.colors.calendar_button_today} !important;
    color: ${({ theme }) => theme.colors.calendar_text} !important;
  }
  .fc-button[title=${STRINGS.calendar.legend}],
  .fc-button[title=${STRINGS.settings.refreshText}] {
    background: ${({ theme }) => theme.colors.border_color};
    border: 1px solid ${({ theme }) => theme.colors.border_color};
  }
  // event
  .fc-timegrid-event-harness,
  .fc-timegrid-event-harness-inset,
  .fc-event,
  .fc-event-main {
    z-index: 10 !important;
    background: ${({ theme }) => theme.colors.calendar_event_bg} !important;
  }
`;

const VisitsCalendarScreen = (props) => {
  const {
    status,
    getCalendarData,
    isLegendModalVisible,
    setIsLegendModalVisible,
    isRefreshing,
    onRefresh,
    // eslint-disable-next-line no-unused-vars
    error,
  } = props;
  const daysOfWeek = [1, 2, 3, 4, 5, 6];
  const startTime = '08:00';
  const endTime = '20:00';

  const renderLegend = () => {
    return (
      <CalendarLegendModal visible={isLegendModalVisible} setVisible={setIsLegendModalVisible} />
    );
  };

  const renderEvent = (data) => {
    const { event, view } = data;
    // eslint-disable-next-line no-underscore-dangle
    const item = event._def;
    const { extendedProps } = item ?? {};
    const { resource } = extendedProps;
    const month = view.type === 'dayGridMonth';
    const week = view.type === 'timeGridWeek';

    return (
      <CalendarTooltip resource={resource} month={month} week={week}>
        <VisitPreviewWebCalendar item={resource} month={month} week={week} />
      </CalendarTooltip>
    );
  };

  const renderCalendar = () => {
    return (
      <Card variant="calendarCard">
        <StyleWrapper>
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
            customButtons={{
              refresh: {
                text: STRINGS.settings.refreshText,
                click: () => onRefresh(),
              },
              legend: {
                text: STRINGS.calendar.legend,
                click: () => setIsLegendModalVisible(true),
              },
            }}
            headerToolbar={{
              left: 'timeGridDay,timeGridWeek,dayGridMonth listWeek',
              center: 'title',
              right: 'legend refresh prev,today,next',
            }}
            initialView="timeGridWeek"
            nowIndicator
            dayHeaderClassNames={styles.dayHeader}
            dayCellClassNames={styles.dayHeader}
            hiddenDays={[0]}
            slotDuration="00:30:00"
            slotLabelInterval={{ minutes: 60 }}
            slotLabelFormat={{
              hour: 'numeric',
              minute: '2-digit',
              omitZeroMinute: false,
              meridiem: 'short',
            }}
            fixedWeekCount={false}
            firstDay={1}
            aspectRatio={2.1}
            slotEventOverlap={false}
            allDaySlot={false}
            dayMaxEventRows={4}
            eventMaxStack={3}
            rerenderDelay={500}
            navLinks
            selectable
            themeSystem="standard"
            eventMinHeight={50}
            eventShortHeight={100}
            locale={plLocale}
            businessHours={[{ daysOfWeek, startTime, endTime }]}
            editable
            events={getCalendarData}
            eventBackgroundColor={theme.colors.success}
            eventBorderColor="transparent"
            eventContent={renderEvent}
            eventTimeFormat={{
              hour: 'numeric',
              minute: '2-digit',
              meridiem: false,
            }}
            select={(item) => console.log('item', item)}
          />
        </StyleWrapper>
        {renderLegend()}
      </Card>
    );
  };

  if (status === 'authenticated') {
    return (
      <ThemeProvider theme={theme}>
        <Layout>
          <React.Fragment>
            <SEO title={COMMON_STRINGS.seo.calendar} />
            <Flex sx={styles.calendarContainer}>
              {isRefreshing && <DimLoadingIndicator />}
              {renderCalendar()}
            </Flex>
          </React.Fragment>
        </Layout>
      </ThemeProvider>
    );
  }
  return (
    <ThemeProvider theme={theme}>
      <Layout>
        <SEO title={COMMON_STRINGS.seo.loading} />
        <Container sx={styles.container}>
          <Flex sx={styles.spinnerContainer}>
            <Spinner size={60} />
          </Flex>
        </Container>
      </Layout>
    </ThemeProvider>
  );
};

VisitsCalendarScreen.propTypes = {
  isRefreshing: PropTypes.bool.isRequired,
  onRefresh: PropTypes.func.isRequired,
  error: PropTypes.string,
};

VisitsCalendarScreen.defaultProps = {
  error: undefined,
};

export default VisitsCalendarScreen;
